import { useEffect, useRef, useState } from "react";
import type { FC } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Skeleton,
  TableContainer,
  Paper,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  TableSortLabel,
  Tooltip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
// import Scrollbar from "../shared/Scrollbar";
import SearchIcon from "../../icons/Search";
import { IColumn } from "./tableModel";
import { dateFormat } from "../../constants";

interface ITable {
  columns: IColumn[];
  dataList: any[];
  isLoading: boolean;
  isSearchable?: boolean;
  onClickDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  commitData: any;
  setDataList: any;
  handleEditColumn: any;
  columnEditing: any;
  handleColumnEdit: any;
  onColumnDelete?: any;
  validateInput?: any;
  // children: React.ReactNode;
}
type Order = "asc" | "desc";
interface EnhancedTableProps {
  columns: IColumn[];
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  onClickDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  order: Order;
  orderBy: string;
  handleEditColumn: any;
  onColumnDelete?: any;
  // rowCount: number;
}

const escapeRegExpMatch = (s) => {
  return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
};
const isExactMatch = (str, match) => {
  return new RegExp(`\\b${escapeRegExpMatch(match)}\\b`).test(str);
};

function descendingComparator<T>(a: T, b: T, orderBy: any) {
  const frst: any =
    typeof a[orderBy] === "string" ? a[orderBy].toLowerCase() : a[orderBy];
  const scnd: any =
    typeof b[orderBy] === "string" ? b[orderBy].toLowerCase() : b[orderBy];
  if (scnd < frst) {
    return -1;
  }
  if (scnd > frst) {
    return 1;
  }
  return 0;
}
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const checkType = (data, dataType) => {
  if (dataType === "boolean") {
    return data ? "YES" : "NO";
  }
  if (dataType === "Date") {
    return dateFormat(data);
    // return ;
  }
  return data;
};

const Cell = ({
  column,
  value,
  idx,
  editable,
  rowKey,
  commitData,
  setDataList,
  columnEditing,
  handleColumnEdit,
  validateInput,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const [error, setError] = useState("");

  const handleDoubleClick = () => {
    if (editable === true && columnEditing === null) {
      setIsEditing(true);
    }
  };

  const handleColumnChange = (event) => {
    const value = event.target.value;
    const validationError = validateInput(value);
    setEditedValue(value);
    setError(validationError);
    if (validationError) {
      return;
    }
    if (columnEditing) {
      handleColumnEdit(rowKey, column, value);
    } else if (!validationError) {
      if (value.length <= column.maxlength) {
        setError("");
      } else {
        setError(`Value cannot exceed ${column.maxlength} characters`);
      }
    }
  };

  const handleBlur = () => {
    const validationError = validateInput(editedValue);
    if (validationError) {
      setError(validationError);
    } else if (editedValue.length > column.maxlength) {
      setError(`Value cannot exceed ${column.maxlength} characters`);
    } else {
      setError("");
      if (!columnEditing && !validationError) {
        commitData(rowKey, column, editedValue);
        setDataList((prevDataList) => {
          const newDataList = [...prevDataList];
          newDataList[rowKey][column.field] = editedValue;
          return newDataList;
        });
        setIsEditing(false);
      }
    }
  };

  const shouldAutoFocus = () => {
    return columnEditing === column.field && rowKey === 0;
  };

  return (
    <TableCell
      align={column.align}
      key={`${rowKey}-${idx}`}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: column.width,
        cursor: editable ? "pointer" : "default",
      }}
      onDoubleClick={handleDoubleClick}
    >
      {columnEditing === column.field || isEditing ? (
        <TextField
          key={`${rowKey}-${idx}`}
          size="small"
          value={editedValue}
          onChange={handleColumnChange}
          onBlur={handleBlur}
          autoFocus={isEditing || shouldAutoFocus()}
          error={Boolean(error)}
          helperText={error}
        />
      ) : (
        <Tooltip title={editedValue}>
          <div>{editedValue}</div>
        </Tooltip>
      )}
    </TableCell>
  );
};

function Row(props) {
  const {
    row,
    columns,
    onClickDelete,
    rowKey,
    commitData,
    setDataList,
    columnEditing,
    handleColumnEdit,
    validateInput,
  } = props;
  return (
    <>
      <TableRow hover role="checkbox" tabIndex={-1} key={rowKey}>
        {columns.map((column, idx) => (
          <Cell
            key={idx.toString()}
            column={column}
            value={row[column.field]}
            rowKey={rowKey}
            idx={idx}
            editable={column.editable}
            commitData={commitData}
            setDataList={setDataList}
            columnEditing={columnEditing}
            handleColumnEdit={handleColumnEdit}
            validateInput={validateInput}
          />
        ))}
        {onClickDelete && (
          <TableCell>
            {
              <IconButton
                sx={{ p: "5px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDelete(row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            }
          </TableCell>
        )}
      </TableRow>
    </>
  );
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    order,
    orderBy,
    columns,
    onRequestSort,
    onClickDelete,
    handleEditColumn,
    onColumnDelete,
  } = props;
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const [showIcons, setShowIcons] = useState(null);

  return (
    <TableHead>
      <TableRow>
        {columns.map((headCell, i) => (
          <TableCell
            width={headCell.width}
            key={i.toString()}
            sortDirection={orderBy === headCell.headerName ? order : false}
            onMouseEnter={() => setShowIcons(i)}
            onMouseLeave={() => setShowIcons(null)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <TableSortLabel
                active={orderBy === headCell.field}
                direction={orderBy === headCell.field ? order : "asc"}
                onClick={createSortHandler(headCell.field)}
              >
                {headCell.headerName}
              </TableSortLabel>
              {handleEditColumn && headCell.editable && showIcons === i && (
                <span style={{ display: "flex" }}>
                  <IconButton onClick={() => handleEditColumn(headCell.field)}>
                    {headCell.editIcon}
                  </IconButton>
                  <IconButton onClick={() => onColumnDelete(headCell.field)}>
                    {headCell.deleteIcon}
                  </IconButton>
                </span>
              )}
            </div>
          </TableCell>
        ))}
        {onClickDelete && <TableCell width="95px" />}
      </TableRow>
    </TableHead>
  );
}

const CellEditTable: FC<ITable> = (props) => {
  const {
    columns = [],
    isLoading,
    isSearchable = true,
    dataList,
    onClickDelete,
    commitData,
    setDataList,
    handleEditColumn,
    columnEditing,
    handleColumnEdit,
    onColumnDelete,
    validateInput,
  } = props;
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState("");
  const searchRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableRef = useRef<HTMLTableElement>(null);
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputString = event.target.value?.toLowerCase(); // Keep spaces intact
    searchRef.current.value = inputString;

    const columnData = [...columns].filter((x) => x.isFilter === undefined || x.isFilter);

    const filterResult = dataList.filter((data) => {
      const result = columnData.map(({ field, dataType }) => checkType(data[field], dataType));
      const rowString = Object.values(result).join(" ").toLowerCase(); // Preserve spaces

      if (inputString.startsWith('"') && inputString.endsWith('"')) {
        return isExactMatch(rowString, inputString.slice(1, -1)); // Remove only the first and last quote
      }

      return rowString.includes(inputString);
    });

    setTableData(filterResult);
  };

  useEffect(() => {
    if (isSearchable && searchRef?.current.value) {
      searchRef.current.value = "";
    }
    setTableData(dataList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList]);
  return (
    <>
      <Box>
        <Grid alignItems="center" container justifyContent="space-between">
          {isSearchable && (
            <Box
              sx={{
                m: 2,
                maxWidth: "100%",
                alignItems: "center",
                display: "flex",
                width: 300,
              }}
            >
              <TextField
                fullWidth
                inputRef={searchRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchRef?.current?.value ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (searchRef.current) {
                            searchRef.current.value = "";
                          }
                          handleQueryChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        ✖
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  style: {
                    height: 43,
                  },
                }}
                onChange={handleQueryChange}
                placeholder="Search"
                value={searchRef?.current?.value || ""}
                variant="outlined"
              />
            </Box>
          )}
        </Grid>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer component={Paper} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {/* <Scrollbar> */}
            {isLoading ? (
              <>
                <Skeleton sx={{ ml: 2, mr: 2, height: 70 }} />
                {Array.from(Array(5).keys()).map((index) => (
                  <Skeleton
                    key={index}
                    sx={{ ml: 2, mr: 2, mt: -2, height: 70 }}
                  />
                ))}
              </>
            ) : (
              <>
                <Table aria-label="collapsible table" sx={{ tableLayout: 'auto', width: 'max-content' }} ref={tableRef} stickyHeader>
                  <EnhancedTableHead
                    columns={columns}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    onClickDelete={onClickDelete}
                    handleEditColumn={handleEditColumn}
                    onColumnDelete={onColumnDelete}
                  />
                  <TableBody>
                    {stableSort(tableData, getComparator(order, orderBy))
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, idx) => (
                        <Row
                          key={idx.toString()}
                          rowKey={row.id}
                          row={row}
                          columns={columns}
                          onClickDelete={onClickDelete}
                          commitData={commitData}
                          setDataList={setDataList}
                          columnEditing={columnEditing}
                          handleColumnEdit={handleColumnEdit}
                          validateInput={validateInput}
                        />
                      ))}
                  </TableBody>
                </Table>
              </>
            )}
            {!isLoading && tableData.length === 0 && (
              <>
                <Box
                  sx={{
                    height: 200,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ pl: 0 }}>
                    No data found!
                  </Typography>
                </Box>
              </>
            )}
            {/* </Scrollbar> */}
          </TableContainer>
        </Box>
        <Box sx={{ width: '100%' }}>
          {tableData.length > 0 && (
            <TablePagination
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

CellEditTable.propTypes = {
  columns: PropTypes.array.isRequired,
  dataList: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSearchable: PropTypes.bool,
  onClickDelete: PropTypes.func,
  columnEditing: PropTypes.any,
  handleColumnEdit: PropTypes.func,
  onColumnDelete: PropTypes.func,
  validateInput: PropTypes.func,
};
Row.propTypes = {
  columns: PropTypes.array,
  onClickDelete: PropTypes.func,
  row: PropTypes.any,
  rowKey: PropTypes.number,
  commitData: PropTypes.func,
  setDataList: PropTypes.any,
  columnEditing: PropTypes.any,
  handleColumnEdit: PropTypes.func,
  validateInput: PropTypes.func,
};
export default CellEditTable;
